/**
 * Recurring > schedule detail (tracker item 29).
 *
 * Summary, billing (per visit or flat contract terms, next billing date and
 * billed periods with their invoices, editable; tracker item 34), the next
 * 10 dates from GET /:id/preview, skipped dates (add and remove), Generate
 * now (created count and each failed date's reason), and Pause / Resume /
 * Cancel with a confirm. Write controls are for ADMIN / OFFICE_MANAGER only;
 * TECHNICIAN sees the page read-only.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createAPIClient, IRecurringJobsApi } from '../api';
import * as types from '../api/types';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/apiError';
import { canManageRecurring } from '../utils/permissions';
import { WEEKDAY_SHORT, dayOfWeek, describeRecurrence, isDateOnly } from '../utils/recurrence';
import { formatJobDate, formatJobMoney } from '../utils/jobPresentation';
import { inputClass, labelClass, primaryButtonClass, rowActionClass, rowDangerActionClass, secondaryButtonClass } from '../components/formStyles';
import { RecurringStatusBadge } from './RecurringListPage';
import { RecurringBillingFields } from './RecurringBillingFields';
import { invoiceStatus } from './invoiceUi';
import { FLAT_CONTRACT_BILLING_PATH } from './flatContractBillingUi';
import { formatTime, visitTimeLabel } from './dispatchUi';
import {
  BillingForm,
  RECURRING_PATH,
  billingFormErrors,
  billingFormFrom,
  billingTermsLabel,
  buildBillingTerms,
  generationSummary,
  recurringNewPath,
  scheduleAddress,
  templateProblem,
  utcToday,
} from './recurringUi';
import { Toast } from '../components/Toast';

const PREVIEW_LIMIT = 10;

function dayLabel(date: string): string {
  return isDateOnly(date) ? `${WEEKDAY_SHORT[dayOfWeek(date)]} ${formatJobDate(date)}` : date;
}

function occurrenceLabel(occurrence: types.RecurringPreview['occurrences'][number]): string {
  if (occurrence.willGenerate) return 'Will generate';
  if (occurrence.reason === 'Date is skipped') return 'Skipped';
  if (occurrence.reason === 'Occurrence already exists') return 'Already generated';
  return occurrence.reason || 'Will not generate';
}

const STATUS_CONFIRM: Record<types.RecurringScheduleStatus, string> = {
  PAUSED: 'Pause this schedule? No jobs are generated while it is paused. Jobs already generated are not changed.',
  ACTIVE: 'Resume this schedule? Generate now will then create a job for every due date that does not have one, including dates that passed while it was paused (unless you skip them first).',
  CANCELLED: 'Cancel this schedule? It stops generating jobs for good. Jobs already generated are not changed.',
};

export function RecurringDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const api = useMemo<IRecurringJobsApi>(() => createAPIClient(), []);
  const { user } = useAuth();
  const canManage = canManageRecurring(user);
  const [schedule, setSchedule] = useState<types.RecurringScheduleDetail | null>(null);
  const [billingForm, setBillingForm] = useState<BillingForm | null>(null);
  const [billingTouched, setBillingTouched] = useState(false);
  const [preview, setPreview] = useState<types.RecurringPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<types.RecurringGenerationResult | null>(null);
  const [skipDate, setSkipDate] = useState('');

  const loadPreview = useCallback(async () => {
    try {
      setPreviewError(null);
      setPreview(await api.previewRecurringOccurrences(id, PREVIEW_LIMIT));
    } catch (cause) {
      setPreview(null);
      setPreviewError(getErrorMessage(cause, 'Could not load upcoming dates'));
    }
  }, [api, id]);

  const load = useCallback(async () => {
    try {
      setLoadError(null);
      const detail = await api.getRecurringSchedule(id);
      setSchedule(detail);
    } catch (cause) {
      // Keep a schedule already on screen; the error is shown either way
      setLoadError(getErrorMessage(cause, 'Could not load the schedule'));
    }
  }, [api, id]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([load(), loadPreview()]).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [load, loadPreview]);

  /** Run a write, then refresh the schedule and its upcoming dates. */
  async function run(label: string, action: () => Promise<unknown>, fallback: string) {
    setBusy(label);
    setActionError(null);
    try {
      await action();
      await Promise.all([load(), loadPreview()]);
      return true;
    } catch (cause) {
      setActionError(getErrorMessage(cause, fallback));
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function changeStatus(status: types.RecurringScheduleStatus) {
    if (!window.confirm(STATUS_CONFIRM[status])) return;
    setResult(null);
    await run(status, () => api.updateRecurringSchedule(id, { status }), 'Could not change the status');
  }

  async function generate() {
    setResult(null);
    await run('generate', async () => {
      const outcome = await api.generateRecurringJobs(id);
      setResult({ ...outcome, errors: outcome.errors ?? [] });
    }, 'Could not generate jobs');
  }

  async function addSkip(date: string) {
    if (!isDateOnly(date)) { setActionError('Enter a valid date to skip.'); return; }
    const ok = await run(`skip-${date}`, () => api.addRecurringSkippedDate(id, date), 'Could not skip the date');
    if (ok) setSkipDate('');
  }

  function editBilling() {
    if (!schedule) return;
    setBillingForm(billingFormFrom(schedule));
    setBillingTouched(false);
  }

  async function saveBilling(event: React.FormEvent) {
    event.preventDefault();
    if (!billingForm) return;
    setBillingTouched(true);
    if (Object.keys(billingFormErrors(billingForm)).length > 0) return;
    const switchingToFlat = schedule?.billingModel !== 'FLAT_CONTRACT' && billingForm.model === 'FLAT_CONTRACT';
    const switchingToPerVisit = schedule?.billingModel === 'FLAT_CONTRACT' && billingForm.model === 'PER_VISIT';
    if (switchingToFlat && !window.confirm(
      'Bill this schedule as a flat contract? Billing starts with the current period; earlier periods are not billed. '
      + 'Visits generated from now on are covered by the contract and cannot be invoiced on their own. Visits already generated keep per-visit billing.'
    )) return;
    if (switchingToPerVisit && !window.confirm(
      'Switch back to per-visit billing? Invoices already issued for contract periods are kept (cancel one if it should not stand). '
      + 'Visits generated from now on are invoiced per visit; visits already generated under the contract stay covered by it.'
    )) return;
    const ok = await run('billing', () => api.updateRecurringSchedule(id, buildBillingTerms(billingForm)), 'Could not save the billing');
    if (ok) setBillingForm(null);
  }

  async function removeSkip(date: string) {
    await run(`unskip-${date}`, () => api.removeRecurringSkippedDate(id, date), 'Could not remove the skipped date');
  }

  if (loading) return <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8"><p role="status">Loading schedule…</p></main>;
  if (!schedule) {
    return (
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Link to={RECURRING_PATH} className="text-sm text-blue-700 hover:underline">← Recurring services</Link>
        {loadError && <Toast kind="error">{loadError}</Toast>}<p className="mt-5 text-gray-700">{loadError ? 'The schedule could not be loaded.' : 'Schedule not found.'}</p>
      </main>
    );
  }

  const status = schedule.status as types.RecurringScheduleStatus;
  const problem = templateProblem(schedule);
  const canGenerate = canManage && status === 'ACTIVE' && !problem;
  const skipped = [...schedule.skippedDates].sort();
  const occurrences = preview?.occurrences ?? [];
  const nextVisit = occurrences.find((o) => o.willGenerate)?.date;
  const isBusy = busy !== null;

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <Link to={RECURRING_PATH} className="text-sm text-blue-700 hover:underline">← Recurring services</Link>
        <div className="flex flex-wrap justify-between items-start gap-3 mt-2">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 break-words">{describeRecurrence(schedule.frequency, schedule.interval, schedule.frequencyDetails)}</h1>
            <p className="text-gray-600 text-sm break-words">{schedule.customer.name} · {scheduleAddress(schedule.property)}</p>
          </div>
          <RecurringStatusBadge status={schedule.status} />
        </div>
      </div>

      {actionError && <Toast kind="error">{actionError}</Toast>}
      {loadError && <Toast kind="error">Could not refresh the schedule: {loadError}</Toast>}
      {problem && <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-900">{problem}</div>}

      <section className="bg-white rounded-lg shadow p-5 sm:p-7">
        <h2 className="text-lg font-semibold mb-4">Schedule</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm break-words">
          <div><dt className="text-gray-500">Customer</dt><dd><Link to={`/customers/${schedule.customerId}`} className="text-blue-700 hover:underline">{schedule.customer.name}</Link></dd></div>
          <div><dt className="text-gray-500">Property</dt><dd>{scheduleAddress(schedule.property)}</dd></div>
          <div><dt className="text-gray-500">Pattern</dt><dd>{describeRecurrence(schedule.frequency, schedule.interval, schedule.frequencyDetails)}</dd></div>
          <div><dt className="text-gray-500">Runs</dt><dd>{formatJobDate(schedule.startDate)} to {schedule.endDate ? formatJobDate(schedule.endDate) : 'no end date'}</dd></div>
          <div>
            <dt className="text-gray-500">Template job</dt>
            <dd>
              {schedule.templateJob?.id ? (
                <Link to={`/jobs/${schedule.templateJob.id}`} className="text-blue-700 hover:underline">
                  Draft job {schedule.templateJob.id.slice(0, 8)}
                </Link>
              ) : 'Deleted'}
              {schedule.templateJob?.status && schedule.templateJob.status !== types.JobStatus.DRAFT && (
                <span className="ml-2 text-amber-800">({schedule.templateJob.status})</span>
              )}
            </dd>
          </div>
          <div><dt className="text-gray-500">Visit time</dt><dd>{visitTimeLabel(schedule.templateJob)}</dd></div>
          <div><dt className="text-gray-500">Billing</dt><dd>{billingTermsLabel(schedule)}</dd></div>
          <div><dt className="text-gray-500">Next visit to generate</dt><dd>{status === 'CANCELLED' ? '—' : nextVisit ? dayLabel(nextVisit) : '—'}</dd></div>
          <div><dt className="text-gray-500">Last generated</dt><dd>{schedule.lastGeneratedDate ? dayLabel(schedule.lastGeneratedDate) : 'Never'}</dd></div>
          <div>
            <dt className="text-gray-500">Next run starts from</dt>
            <dd>{schedule.nextScheduledDate ? formatJobDate(schedule.nextScheduledDate) : '—'}</dd>
          </div>
          <div><dt className="text-gray-500">Generated jobs</dt><dd><Link to={`/jobs?customerId=${encodeURIComponent(schedule.customerId)}`} className="text-blue-700 hover:underline">View this customer's jobs</Link></dd></div>
          {schedule.notes && <div className="sm:col-span-2"><dt className="text-gray-500">Notes</dt><dd className="whitespace-pre-wrap">{schedule.notes}</dd></div>}
        </dl>
        <p className="mt-4 text-xs text-gray-500">
          The property and template job cannot be changed on an existing schedule (billing can, below). To change them, cancel this schedule and{' '}
          {canManage ? <Link to={recurringNewPath({ customerId: schedule.customerId, propertyId: schedule.propertyId })} className="text-blue-700 underline">create a new one</Link> : 'create a new one'}.
        </p>
      </section>

      <section className="bg-white rounded-lg shadow p-5 sm:p-7" aria-labelledby="recurring-billing-heading">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
          <h2 id="recurring-billing-heading" className="text-lg font-semibold">Billing</h2>
          {canManage && !billingForm && status !== 'CANCELLED' && (
            <button type="button" className={`${rowActionClass} min-h-[44px] sm:min-h-0`} disabled={isBusy} onClick={editBilling}>Edit billing</button>
          )}
        </div>
        {billingForm ? (
          <form onSubmit={(event) => void saveBilling(event)} noValidate className="space-y-4">
            <fieldset disabled={isBusy}>
              <legend className="sr-only">Billing terms</legend>
              <RecurringBillingFields
                idPrefix="recurring-edit-billing"
                form={billingForm}
                errors={billingTouched ? billingFormErrors(billingForm) : {}}
                onChange={(patch) => setBillingForm((current) => (current ? { ...current, ...patch } : current))}
              />
            </fieldset>
            <div className="flex flex-wrap gap-3">
              <button type="submit" className={`${primaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={isBusy}>{busy === 'billing' ? 'Saving…' : 'Save billing'}</button>
              <button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={isBusy} onClick={() => setBillingForm(null)}>Cancel</button>
            </div>
          </form>
        ) : schedule.billingModel === 'FLAT_CONTRACT' ? (
          <>
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div><dt className="text-gray-500">Contract</dt><dd>{billingTermsLabel(schedule)} (plus tax)</dd></div>
              <div><dt className="text-gray-500">Billed from</dt><dd>{schedule.billingStartDate ? formatJobDate(schedule.billingStartDate) : '—'}</dd></div>
              <div><dt className="text-gray-500">Next billing date</dt><dd>{schedule.nextBillingDate ? formatJobDate(schedule.nextBillingDate) : '—'}</dd></div>
            </dl>
            <p className="mt-3 text-sm text-gray-600">
              One invoice per billing period, issued at the start of the period. The visit jobs are covered by the contract and are not
              invoiced on their own. {canManage && <>Bill due periods on <Link to={FLAT_CONTRACT_BILLING_PATH} className="text-blue-700 underline">Bill flat contracts</Link>.</>}
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-600">Per visit: each generated job is invoiced like any other job.</p>
        )}
        {(schedule.billingPeriods ?? []).length > 0 && (
          <table className="responsive-table mt-4 min-w-full divide-y divide-gray-200 text-sm">
            <caption className="text-left text-sm font-semibold text-gray-900 pb-2">Billed periods</caption>
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-2 text-left font-medium text-gray-600">Period</th>
                <th scope="col" className="px-4 py-2 text-left font-medium text-gray-600">Invoice</th>
                <th scope="col" className="px-4 py-2 text-right font-medium text-gray-600">Amount</th>
                <th scope="col" className="px-4 py-2 text-left font-medium text-gray-600">Billed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(schedule.billingPeriods ?? []).map((period) => (
                <tr key={period.periodStartDate}>
                  <td data-label="Period" data-primary className="px-4 py-2 whitespace-nowrap">{formatJobDate(period.periodStartDate)} – {formatJobDate(period.periodEndDate)}</td>
                  <td data-label="Invoice" className="px-4 py-2">
                    {canManage
                      ? <Link to={`/invoices/${period.invoiceId}`} className="text-blue-700 hover:underline">#{period.invoiceNumber}</Link>
                      : `#${period.invoiceNumber}`}
                    <span className="ml-2 text-gray-500">{invoiceStatus(period.invoiceStatus)}</span>
                  </td>
                  <td data-label="Amount" className="px-4 py-2 text-right tabular-nums">{formatJobMoney(period.amount)}</td>
                  <td data-label="Billed" className="px-4 py-2 text-gray-600">{period.trigger === 'AUTOMATIC' ? 'Automatically' : 'Manually'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="bg-white rounded-lg shadow p-5 sm:p-7" aria-labelledby="recurring-generate-heading">
        <h2 id="recurring-generate-heading" className="text-lg font-semibold mb-2">Generate jobs</h2>
        <p className="text-sm text-gray-600">
          Jobs are created manually with <strong>Generate now</strong>, unless the server's background generator is turned on
          (<code>RECURRING_JOBS_ENABLED</code>, off by default). A run creates a scheduled job for every date in the pattern up to today
          ({formatJobDate(utcToday())}, UTC) that is not skipped and has no job yet, up to 100 per run. Future dates are not created early.
          Running it again never creates duplicates.
        </p>
        {canManage && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button type="button" className={`${primaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={!canGenerate || isBusy} onClick={() => void generate()}>
              {busy === 'generate' ? 'Generating…' : 'Generate now'}
            </button>
            {status !== 'ACTIVE' && <span className="text-sm text-gray-600">Only active schedules can generate jobs.</span>}
          </div>
        )}
        {result && (
          <Toast
            kind={result.failedCount > 0 ? 'warning' : 'success'}
            id={`${generationSummary(result)}|${(result.errors ?? []).map((entry) => `${entry.date}:${entry.reason}`).join(',')}`}
          >
            <p className="font-semibold">{generationSummary(result)}</p>
            {result.errors && result.errors.length > 0 && (
              <ul className="mt-2 list-disc pl-5 space-y-1">
                {result.errors.map((entry) => (
                  <li key={`${entry.date}-${entry.reason}`}><strong>{dayLabel(entry.date)}</strong>: {entry.reason}</li>
                ))}
              </ul>
            )}
            {result.generatedCount > 0 && (
              <p className="mt-2"><Link to={`/jobs?customerId=${encodeURIComponent(schedule.customerId)}&status=SCHEDULED`} className="underline">View scheduled jobs</Link></p>
            )}
          </Toast>
        )}
      </section>

      <section className="bg-white rounded-lg shadow p-5 sm:p-7" aria-labelledby="recurring-upcoming-heading">
        <h2 id="recurring-upcoming-heading" className="text-lg font-semibold mb-4">Upcoming dates</h2>
        {previewError ? (
          <><Toast kind="error">{previewError}</Toast><p className="text-gray-600 text-sm">Upcoming dates could not be loaded.</p></>
        ) : occurrences.length === 0 ? (
          <p className="text-gray-600 text-sm">No more dates: the schedule has ended or nothing is left to generate.</p>
        ) : (
          <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
            <caption className="sr-only">Next {PREVIEW_LIMIT} dates</caption>
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-2 text-left font-medium text-gray-600">Date</th>
                <th scope="col" className="px-4 py-2 text-left font-medium text-gray-600">Status</th>
                {canManage && <th scope="col" className="px-4 py-2 text-right font-medium text-gray-600"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {occurrences.map((occurrence) => (
                <tr key={occurrence.date} className={occurrence.willGenerate ? undefined : 'text-gray-500'}>
                  <td data-label="Date" data-primary className="px-4 py-2 whitespace-nowrap">{dayLabel(occurrence.date)}{schedule.templateJob?.scheduledStartTime && <span className="text-gray-500"> · {formatTime(schedule.templateJob.scheduledStartTime)}</span>}</td>
                  <td data-label="Status" className="px-4 py-2">{occurrenceLabel(occurrence)}</td>
                  {canManage && (
                    <td className="px-4 py-2 text-right">
                      {occurrence.willGenerate && status !== 'CANCELLED' && (
                        <button type="button" className={`${rowActionClass} min-h-[44px] sm:min-h-0`} disabled={isBusy} onClick={() => void addSkip(occurrence.date)} aria-label={`Skip ${occurrence.date}`}>
                          Skip
                        </button>
                      )}
                      {occurrence.reason === 'Date is skipped' && (
                        <button type="button" className={`${rowActionClass} min-h-[44px] sm:min-h-0`} disabled={isBusy} onClick={() => void removeSkip(occurrence.date)} aria-label={`Unskip ${occurrence.date}`}>
                          Unskip
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="bg-white rounded-lg shadow p-5 sm:p-7" aria-labelledby="recurring-skipped-heading">
        <h2 id="recurring-skipped-heading" className="text-lg font-semibold mb-2">Skipped dates</h2>
        <p className="text-sm text-gray-600 mb-4">No job is generated on a skipped date. Skipping a date that already has a job does not remove that job.</p>
        {skipped.length === 0 ? (
          <p className="text-sm text-gray-500">No skipped dates.</p>
        ) : (
          <ul className="divide-y divide-gray-100 mb-4">
            {skipped.map((date) => (
              <li key={date} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span>{dayLabel(date)}</span>
                {canManage && (
                  <button type="button" className={`${rowDangerActionClass} min-h-[44px] sm:min-h-0`} disabled={isBusy} onClick={() => void removeSkip(date)} aria-label={`Remove skipped date ${date}`}>
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {canManage && status !== 'CANCELLED' && (
          <form className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3" onSubmit={(event) => { event.preventDefault(); void addSkip(skipDate); }}>
            <div className="w-full sm:w-auto">
              <label htmlFor="recurring-skip-date" className={labelClass}>Skip a date</label>
              <input id="recurring-skip-date" type="date" className={inputClass} value={skipDate} min={schedule.startDate} max={schedule.endDate} onChange={(event) => setSkipDate(event.target.value)} />
            </div>
            <button type="submit" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={isBusy || !skipDate}>Skip date</button>
          </form>
        )}
      </section>

      {canManage && (
        <section className="bg-white rounded-lg shadow p-5 sm:p-7" aria-labelledby="recurring-status-heading">
          <h2 id="recurring-status-heading" className="text-lg font-semibold mb-3">Status</h2>
          {status === 'CANCELLED' ? (
            <p className="text-sm text-gray-600">This schedule is cancelled. Create a new schedule to start the service again.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {status === 'ACTIVE' && (
                <button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={isBusy} onClick={() => void changeStatus('PAUSED')}>Pause</button>
              )}
              {status === 'PAUSED' && (
                <button type="button" className={`${primaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={isBusy} onClick={() => void changeStatus('ACTIVE')}>Resume</button>
              )}
              <button type="button" className={`${secondaryButtonClass} text-red-700 min-h-[44px] sm:min-h-0`} disabled={isBusy} onClick={() => void changeStatus('CANCELLED')}>Cancel schedule</button>
            </div>
          )}
        </section>
      )}
    </main>
  );
}
